import { SEAT_MAX_SCALE, SEAT_MIN_SCALE, SEAT_UNIT_FRACTION } from '@movienight/shared';
import type { CSSProperties } from 'react';

/**
 * The one place a seat becomes a position on screen.
 *
 * Shared by the editor and the booking sheet on purpose. They render entirely
 * different things inside the marker — a label and handles, or somebody's face
 * — but if they disagreed by a pixel about *where* it goes, every map would be
 * placed against one picture and read against another, and the symptom would
 * be "the seats are slightly wrong on my phone".
 */

export const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

export const clampScale = (n: number): number =>
  Math.min(SEAT_MAX_SCALE, Math.max(SEAT_MIN_SCALE, n));

/** Degrees, one turn, no negatives — matching the CHECK in 0006. */
export const normalizeRotation = (deg: number): number => ((deg % 360) + 360) % 360;

export interface Placed {
  x: number;
  y: number;
  rotation: number;
  scale: number;
}

/**
 * Everything is a percentage of the container, including the size: `width` in
 * percent with a square aspect ratio, rather than a pixel box, is what makes a
 * map drawn at 360px and the same map drawn at 700px the same map.
 *
 * The rotation is applied *after* the centring translate, so a seat turns
 * about its own middle rather than swinging around the point it is pinned to.
 */
export function seatStyle(seat: Placed): CSSProperties {
  return {
    left: `${seat.x * 100}%`,
    top: `${seat.y * 100}%`,
    width: `${seat.scale * SEAT_UNIT_FRACTION * 100}%`,
    // Centred on its point, and **not** rotated.
    transform: 'translate(-50%, -50%)',
    /**
     * The seat's facing, handed to CSS for the two things that *should* turn
     * with it: the notch on its edge, and an occupant's avatar (DESIGN.md §8
     * asks for the face rotated to the seat).
     *
     * The marker itself deliberately does not rotate. It used to, with the
     * label and the number counter-rotated to stay upright — which works until
     * the angle is 90°, where counter-rotating a label pinned under the marker
     * swings it out sideways across the marker instead of leaving it below.
     * Rotating only the parts that mean something has no such case.
     */
    ['--seat-rot' as string]: `${seat.rotation}deg`,
  } as CSSProperties;
}

/**
 * Where in the image a pointer is, as fractions.
 *
 * Clamped, because a drag that leaves the element still has to land somewhere
 * legal — and the database refuses anything outside [0, 1] (0006), so an
 * unclamped drag would turn into a failed save minutes later.
 */
export function fractionAt(
  event: { clientX: number; clientY: number },
  el: HTMLElement,
): { x: number; y: number } {
  const rect = el.getBoundingClientRect();
  return {
    x: clamp01((event.clientX - rect.left) / rect.width),
    y: clamp01((event.clientY - rect.top) / rect.height),
  };
}
